
/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import isNil from 'lodash.isnil';
import {Notification, Notifications} from '@ion/core/src/types/entities';

/**
 * This function is used to apply an extended attribute override to
 * a Notification state. It takes two parameters: the current state of the Notification
 * and the updated version of the Notification containing the override.
 * The function returns a new Notification object with the applied extended attribute override.
 *
 * @param state The current state of the Notification object that contains the original extended attribute data.
 * @param updated The updated version of the Notification object that contains the override for the extended attribute.
 * @returns The function returns a new Notification object with the applied extended attribute override
 *
 * ```typescript
 *
 *  import {applyExtendedAttributeOverride} from './helpers';
 *
 *  const state = {
 *    id: 1,
 *    title: "Notification 1",
 *    extendedAttribute: {
 *      value: "extended attribute value in state",
 *      // other extended attribute properties...
 *    }
 *   };
 *
 *   const updated = {
 *    id: 1,
 *    title: "Updated Notification 1",
 *    extendedAttribute: {
 *      value: "updated extended attribute value",
 *      // other extended attribute properties...
 *    }
 *   };
 *  const notificationWithOverride = applyExtendedAttributeOverride(state, updated);
 *  console.log(notificationWithOverride);
 * ```
 */
export const applyExtendedAttributeOverride = (state: Notification, updated: Notification) => ({
  ...updated,
  extendedAttribute : {
    ...updated?.extendedAttribute,
    value : state.extendedAttribute?.value || updated?.extendedAttribute?.value
  }
});

/**
 * This function is designed to synchronize the updated notifications received as
 * an array of PromiseSettledResult objects with the existing notifications stored
 * in the results array. The purpose is to update the existing notification entities
 * with the latest data from the updated notifications.
 *
 * @param updatedNotifications It contains the updated notification entities
 * @param results  It represents the existing notification entities that need to be updated.
 * @returns Array of Notifications that have been synchronized with the updated notification data.
 *
 * ```typescript
 *
 * const updatedNotifications = [
 *   Promise.resolve({status: 'fulfilled', value: { id: 1, message: 'Updated notification' }}),
 *   Promise.resolve({ status: 'rejected', reason: 'Error fetching notification'}
 * )];
 * const results = [{ id: 1, message: 'Existing notification' }, { id: 2, message: 'Another existing notification' }];
 * const synchronizedNotifications = synchronizeNotificationEntities(updatedNotifications, results);
 *
 * console.log(synchronizedNotifications);
 * // Output: [{ id: 1, message: 'Updated notification' }, { id: 2, message: 'Another existing notification' }]
 *
 * ```
 */
export const synchronizeNotificationEntities = (
  updatedNotifications: PromiseSettledResult<Notification>[],
  results: Notifications
) => (
  updatedNotifications.map((item, index) => {
    const {status, value} = (item || {}) as any;
    let notification = results[index];
    if (status === 'fulfilled' && !isNil(value)) {
      notification = value;
    }
    return notification;
  })
);
